/* Bronson product reviews: sorting, show more, summary/sort popovers, review popup and write-a-review form */
if (!customElements.get('bronson-product-reviews')) {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea, [tabindex]:not([tabindex="-1"])';

  customElements.define(
    'bronson-product-reviews',
    class BronsonProductReviews extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.list = this.querySelector('[data-bpr-list]');
        this.items = this.list ? [...this.list.querySelectorAll('[data-bpr-item]')] : [];
        this.perPage = Number(this.dataset.perPage) || 5;
        this.shown = this.perPage;
        this.moreWrap = this.querySelector('[data-bpr-more]')?.parentElement;

        this.modals = {};
        this.querySelectorAll('[data-bpr-modal]').forEach((modal) => {
          ['--brv-font', '--brv-star', '--brv-star-empty'].forEach((v) => modal.style.setProperty(v, getComputedStyle(this).getPropertyValue(v)));
          modal.addEventListener('click', (e) => {
            if (e.target.closest('[data-bpr-close]')) this.closeModal();
          });
          modal.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') this.closeModal();
            if (e.key === 'Tab') this.trap(e, modal);
          });
          this.modals[modal.dataset.bprModal] = modal;
          document.body.appendChild(modal);
        });

        this.addEventListener('click', (e) => this.onClick(e));
        this.addEventListener('keydown', (e) => {
          const card = e.target.closest('.bpr-card__main[data-bpr-open]');
          if (card && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this.openReview(card);
          }
          if (e.key === 'Escape') this.closePanels();
        });
        this.onDocClick = (e) => {
          if (!e.target.closest('[data-bpr-panel], [data-bpr-toggle]')) this.closePanels();
        };
        document.addEventListener('click', this.onDocClick);

        this.sortBy(this.dataset.sort || 'automatic');

        // back from a contact form post (success or errors): reopen the form
        if (this.modals.write?.querySelector('[data-bpr-posted]')) {
          requestAnimationFrame(() => this.openModal('write'));
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocClick);
        Object.values(this.modals || {}).forEach((modal) => modal.remove());
      }

      onClick(e) {
        const toggle = e.target.closest('[data-bpr-toggle]');
        if (toggle) return this.togglePanel(toggle);

        const sort = e.target.closest('[data-bpr-sort]');
        if (sort) {
          this.shown = this.perPage;
          this.sortBy(sort.dataset.bprSort);
          this.closePanels();
          return;
        }

        if (e.target.closest('[data-bpr-more]')) {
          this.shown += this.perPage;
          this.render();
          return;
        }

        const write = e.target.closest('[data-bpr-write]');
        if (write) return this.openModal('write', write);

        const open = e.target.closest('[data-bpr-open]');
        if (open && !e.target.closest('a')) this.openReview(open);
      }

      /* ---------- list ---------- */
      sortBy(key) {
        if (!this.list) return;
        const date = (el) => Number(el.dataset.date) || 0;
        const rating = (el) => Number(el.dataset.rating) || 0;
        const compare = {
          newest: (a, b) => date(b) - date(a),
          oldest: (a, b) => date(a) - date(b),
          highest: (a, b) => rating(b) - rating(a) || date(b) - date(a),
          lowest: (a, b) => rating(a) - rating(b) || date(b) - date(a),
          automatic: (a, b) => Number(b.dataset.image) - Number(a.dataset.image) || date(b) - date(a),
        }[key];
        if (compare) {
          this.items.sort(compare);
          this.list.append(...this.items);
        }
        this.querySelectorAll('[data-bpr-sort]').forEach((btn) => {
          const on = btn.dataset.bprSort === key;
          btn.classList.toggle('is-selected', on);
          btn.setAttribute('aria-pressed', on);
        });
        this.render();
      }

      render() {
        this.items.forEach((item, i) => {
          item.hidden = i >= this.shown;
        });
        if (this.moreWrap) this.moreWrap.hidden = this.shown >= this.items.length;
      }

      /* ---------- popovers ---------- */
      togglePanel(toggle) {
        const panel = this.querySelector(`[data-bpr-panel="${toggle.dataset.bprToggle}"]`);
        if (!panel) return;
        const open = panel.hidden;
        this.closePanels();
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', open);
      }

      closePanels() {
        this.querySelectorAll('[data-bpr-panel]').forEach((panel) => {
          panel.hidden = true;
        });
        this.querySelectorAll('[data-bpr-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      }

      /* ---------- modals ---------- */
      openReview(el) {
        const tpl = el.closest('[data-bpr-item]')?.querySelector('[data-bpr-template]');
        const modal = this.modals.review;
        if (!tpl || !modal) return;
        const target = modal.querySelector('[data-bpr-target]');
        target.replaceChildren(tpl.content.cloneNode(true));
        this.openModal('review', el);
        target.scrollTop = 0;
      }

      openModal(name, trigger) {
        const modal = this.modals[name];
        if (!modal) return;
        this.closeModal(true);
        this.active = modal;
        this.trigger = trigger;
        clearTimeout(modal.hideTimer);
        modal.hidden = false;
        void modal.offsetWidth;
        modal.classList.add('is-open');
        document.documentElement.classList.add('bh-scroll-lock');
        requestAnimationFrame(() => {
          const first = name === 'write' ? modal.querySelector('.bpr-rate__input:checked, .bpr-write__input, [data-bpr-close]') : modal.querySelector('.brv-pop__close');
          first?.focus({ preventScroll: true });
        });
      }

      closeModal(instant) {
        const modal = this.active;
        if (!modal) return;
        this.active = null;
        modal.classList.remove('is-open');
        document.documentElement.classList.remove('bh-scroll-lock');
        const hide = () => {
          modal.hidden = true;
          if (modal.dataset.bprModal === 'review') modal.querySelector('[data-bpr-target]').replaceChildren();
        };
        if (instant === true) hide();
        else modal.hideTimer = setTimeout(hide, 400);
        if (instant !== true) this.trigger?.focus({ preventScroll: true });
      }

      trap(e, modal) {
        const items = [...modal.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el.classList.contains('bpr-rate__input'));
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
